import { useState, useEffect } from 'react';
import { AdminLayout } from './AdminDashboard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FileText, MapPin, Truck, Download, XCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['pending', 'processing', 'confirmed', 'paid', 'demo_paid', 'shipped', 'out_for_delivery', 'delivered', 'cancelled', 'cod_pending', 'payment_failed', 'refund_initiated', 'refund_completed'];
const statusColors = { pending: 'bg-yellow-100 text-yellow-800', processing: 'bg-orange-100 text-orange-800', paid: 'bg-green-100 text-green-800', demo_paid: 'bg-blue-100 text-blue-800', shipped: 'bg-purple-100 text-purple-800', out_for_delivery: 'bg-indigo-100 text-indigo-800', delivered: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-red-100 text-red-800', confirmed: 'bg-green-100 text-green-800', cod_pending: 'bg-orange-100 text-orange-800', payment_failed: 'bg-red-100 text-red-800', refund_initiated: 'bg-violet-100 text-violet-800', refund_completed: 'bg-emerald-100 text-emerald-800' };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [trackingOpen, setTrackingOpen] = useState(null);
  const [trackStatus, setTrackStatus] = useState('');
  const [trackNote, setTrackNote] = useState('');

  // Refund modal state
  const [refundModal, setRefundModal] = useState({ open: false, order: null });
  const [refundType, setRefundType] = useState('full'); // 'full' | 'partial'
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('Customer request');
  const [refunding, setRefunding] = useState(false);

  const fetchOrders = () => { setLoading(true); const p = filterStatus !== 'all' ? `?status=${filterStatus}` : ''; axios.get(`${API}/admin/orders${p}`, { withCredentials: true }).then(r => setOrders(r.data.orders)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetchOrders(); }, [filterStatus]); // eslint-disable-line

  const updateStatus = async (oid, status) => { try { await axios.put(`${API}/admin/orders/${oid}/status`, { status }, { withCredentials: true }); toast.success('Status updated'); fetchOrders(); } catch { toast.error('Failed'); } };
  const addTracking = async () => {
    if (!trackStatus) return;
    try { await axios.put(`${API}/admin/orders/${trackingOpen}/tracking`, { status: trackStatus, note: trackNote }, { withCredentials: true }); toast.success('Tracking added'); setTrackingOpen(null); setTrackStatus(''); setTrackNote(''); fetchOrders(); } catch { toast.error('Failed'); }
  };

  const openRefundModal = (order) => {
    setRefundModal({ open: true, order });
    setRefundType('full');
    setRefundAmount('');
    setRefundReason('Customer request');
  };

  const submitRefund = async () => {
    const { order } = refundModal;
    const amount = refundType === 'full' ? null : parseFloat(refundAmount);
    if (refundType === 'partial' && (!amount || amount <= 0)) {
      toast.error('Please enter a valid refund amount'); return;
    }
    if (refundType === 'partial' && amount > order.total) {
      toast.error(`Refund cannot exceed order total ₹${order.total}`); return;
    }
    if (!refundReason.trim()) { toast.error('Please enter a reason'); return; }
    setRefunding(true);
    try {
      const res = await axios.post(`${API}/admin/orders/${order.id}/refund`,
        { amount: refundType === 'full' ? null : amount, reason: refundReason },
        { withCredentials: true }
      );
      toast.success(`Refund initiated! Refund ID: ${res.data.refund_id}`);
      setRefundModal({ open: false, order: null });
      fetchOrders();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  };

  const openInvoice = (order) => {
      const html = `<html><head><meta charset="UTF-8"><title>Invoice ${order.invoice_number}</title>
        <style>*{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Segoe UI',sans-serif;background:#F8F5F2;color:#1C1209;padding:20px;}
        .invoice-wrapper{max-width:750px;margin:20px auto;background:#fff;border:1px solid #E5D9CC;border-radius:12px;padding:40px;box-shadow:0 10px 40px rgba(28,18,9,0.05);}
        h1{color:#C45E2A;font-size:26px;margin-bottom:6px}.meta{color:#8B6A52;font-size:13px;margin-bottom:24px;border-bottom:1px solid #F5E8D8;padding-bottom:12px;}
        .grid-boxes{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px;}
        .customer-box{padding:16px;border:1px solid #F5E8D8;border-radius:8px;background:#FDFBF9;}
        .box-title{font-size:14px;font-weight:700;color:#1C1209;margin-bottom:10px;border-bottom:1px solid #E5D9CC;padding-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;}
        .section{margin-bottom:8px;font-size:13px;line-height:1.4;}.section:last-child{margin-bottom:0;}.label{font-weight:600;font-size:12px;color:#4A3728;display:inline-block;width:75px;vertical-align:top;}
        table{width:100%;border-collapse:collapse;margin:24px 0}th,td{border:1px solid #F5E8D8;padding:12px;text-align:left;font-size:13px}
        th{background:#F9F4EE;font-weight:700;color:#4A3728}.total{font-size:18px;font-weight:700;color:#C45E2A;text-align:right;margin-top:16px}
        .actions{margin:20px auto;text-align:center;max-width:750px;} .btn{background:#C45E2A;color:#fff;border:none;padding:12px 32px;border-radius:100px;font-size:15px;cursor:pointer;margin:0 8px;font-weight:600;transition:0.2s;}
        .btn:hover{background:#1C1209;transform:translateY(-2px);}.btn-outline{background:#fff;color:#C45E2A;border:2px solid #C45E2A}
        @media print{
            body{background:#fff;padding:0;}
            .invoice-wrapper{border:1px solid #000;box-shadow:none;margin:0;border-radius:0;padding:30px;max-width:100%;}
            .customer-box{border:1px solid #E5D9CC !important; -webkit-print-color-adjust: exact;}
            .actions{display:none} 
            table th{background-color:#eee !important; -webkit-print-color-adjust: exact;}
        }
        </style></head><body>
        
        <div class="actions">
            <button class="btn" onclick="window.print()">🖨️ Print Admin Invoice</button>
            <button class="btn btn-outline" onclick="window.close()">✕ Close</button>
        </div>

        <div class="invoice-wrapper">
        <h1 style="display:flex; justify-content:space-between; align-items:center;">
            Pets Valley - Tax Invoice
            <span style="font-size:14px; font-weight:600; color:#8B6A52; background:#F5E8D8; padding:4px 12px; border-radius:20px; border:1px solid #E5D9CC;">SELLER'S COPY</span>
        </h1>
        <div class="meta"><strong>Invoice:</strong> ${order.invoice_number} &nbsp;|&nbsp; <strong>Order No:</strong> ${order.id} &nbsp;|&nbsp; <strong>Date:</strong> ${new Date(order.created_at).toLocaleDateString()}</div>
        
        <div class="grid-boxes">
            <div class="customer-box">
                <div class="box-title">Bill To (Customer)</div>
                <div class="section"><span class="label">Name:</span> <span>${order.user_name || order.shipping_address?.name || ''}</span></div>
                <div class="section"><span class="label">Email:</span> <span>${order.user_email}</span></div>
                <div class="section"><span class="label">Phone:</span> <span>${order.phone||''}</span></div>
                <div class="section"><span class="label">Payment:</span> <span>${order.payment_method === 'cod' ? 'Cash on Delivery' : order.status === 'payment_failed' ? 'Online <span style="color:#dc2626;font-weight:700">(Failed)</span>' : 'Online <span style="color:#16a34a;font-weight:700">(Successful)</span>'}</span></div>
            </div>
            
            <div class="customer-box">
                <div class="box-title">Ship To (Destination)</div>
                <div class="section"><span class="label">Name:</span> <span>${order.shipping_address?.name || order.user_name || ''}</span></div>
                <div class="section"><span class="label" style="margin-top:2px;">Address:</span> <div style="display:inline-block; width:calc(100% - 80px)">${(order.shipping_address?.line1||'')}${order.shipping_address?.line2 ? `<br>${order.shipping_address.line2}` : ''} <br>${(order.shipping_address?.city||'')}, ${(order.shipping_address?.state||'')} - <strong>${(order.shipping_address?.pincode||'')}</strong></div></div>
            </div>
        </div>

        <table><tr><th>S.No.</th><th>Item Description</th><th>Qty</th><th>MRP</th><th>Discount</th><th>Price</th><th>Total</th></tr>
        ${(order.items||[]).map((i, idx) => {
           let desc = i.description ? `<br><span style="font-size:11px;color:#8B6A52">${i.description.substring(0,80)}${i.description.length>80?'...':''}</span>` : '';
           let mrp = i.mrp && i.mrp > i.price ? i.mrp : i.price;
           let discount = mrp - i.price;
           return `<tr>
             <td>${idx+1}</td>
             <td><strong>${i.name}</strong>${desc}</td>
             <td>${i.quantity}</td>
             <td>₹${mrp.toLocaleString('en-IN')}</td>
             <td>₹${discount.toLocaleString('en-IN')}</td>
             <td>₹${i.price.toLocaleString('en-IN')}</td>
             <td><strong>₹${(i.price * i.quantity).toLocaleString('en-IN')}</strong></td>
           </tr>`;
        }).join('')}
        </table>
        
        <div style="display:flex; justify-content: flex-end; margin-top:16px;">
            <div style="text-align:right; min-width:200px;">
                ${(() => {
                    let subtotal = (order.items||[]).reduce((sum, i) => sum + (i.price * i.quantity), 0);
                    return `
                    <div style="font-size:14px; color:#4A3728; margin-bottom:12px; display:flex; justify-content:space-between; border-bottom:1px solid #E5D9CC; padding-bottom:12px;"><span>Subtotal:</span> <span>₹${subtotal.toLocaleString('en-IN', {maximumFractionDigits:2, minimumFractionDigits:2})}</span></div>
                    `;
                })()}
                <div style="font-size:18px; font-weight:700; color:#C45E2A; display:flex; justify-content:space-between;"><span>Total Paid:</span> <span>₹${(order.total||0).toLocaleString('en-IN', {maximumFractionDigits:2, minimumFractionDigits:2})}</span></div>
            </div>
        </div>
        
        <div style="margin-top: 40px; display: flex; justify-content: flex-end;">
            <div style="text-align: center; width: 180px;">
                <div style="font-family: 'Brush Script MT', 'Comic Sans MS', cursive; font-size: 32px; color: #00008B; transform: rotate(-5deg); margin-bottom: 8px;">Pets Valley</div>
                <div style="border-top: 1.5px solid #4A3728; padding-top: 8px; font-size: 12px; font-weight: 700; color: #4A3728; text-transform: uppercase; letter-spacing: 1px;">Authorized Signatory</div>
                <div style="font-size: 10px; color: #8B6A52; margin-top: 3px;">Digitally Verified</div>
            </div>
        </div>
        </div>
        
        </body></html>`;
        
      const win = window.open('','_blank');
      win.document.write(html);
      win.document.close();
  };

  return (
    <AdminLayout active="/admin/orders">
      <div data-testid="admin-orders">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-dark-brown">Orders</h1>
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-[180px] bg-white border-border-custom font-body"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_',' ').toUpperCase()}</SelectItem>)}</SelectContent></Select>
        </div>
        {loading ? <div className="flex justify-center py-20"><div className="spinner"></div></div> : orders.length === 0 ? <div className="text-center py-20 font-body text-light-brown">No orders</div> : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="bg-white rounded-xl border border-border-custom p-5" data-testid={`admin-order-${order.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-3">
                  <div>
                    <div className="font-body text-sm font-medium text-dark-brown">#{order.id} &middot; {order.invoice_number}</div>
                    <div className="font-body text-xs text-light-brown">{order.user_email} &middot; {new Date(order.created_at).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'})}</div>
                    <div className="font-body text-xs text-mid-brown mt-0.5">Payment: {(order.payment_method || 'razorpay').toUpperCase()}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-lg font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'\u20B9'}{order.total?.toLocaleString('en-IN')}</span>
                    <Select value={order.status} onValueChange={v => updateStatus(order.id, v)}><SelectTrigger className={`w-[160px] rounded-full text-xs font-body ${statusColors[order.status]||'bg-gray-100'}`}><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_',' ').toUpperCase()}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {order.items?.map((it, i) => <div key={i} className="bg-cream rounded-lg px-3 py-1.5 font-body text-xs text-dark-brown">{it.name} x{it.quantity} <span className="font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif' }}>{'\u20B9'}{(it.price*it.quantity).toLocaleString('en-IN')}</span></div>)}
                </div>
                {order.shipping_address && <div className="font-body text-xs text-light-brown mb-3">Ship: {order.shipping_address.name}, {order.shipping_address.line1}, {order.shipping_address.city} - {order.shipping_address.pincode}</div>}

                {/* Cancelled Notice */}
                {order.status === 'cancelled' && (
                  <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-3 mb-3">
                    <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-body text-xs font-bold text-red-700">{order.cancelled_by === 'admin' ? 'Cancelled by Admin' : 'Cancelled by Customer'}</span>
                        <span className="bg-red-100 text-red-700 border border-red-200 text-xs font-body font-semibold px-2 py-0.5 rounded-full">{order.cancelled_by === 'admin' ? 'Admin Cancelled' : 'User Cancelled'}</span>
                      </div>
                      {order.cancel_reason && (
                        <p className="font-body text-xs text-red-600">
                          <span className="font-semibold">Reason:</span> {order.cancel_reason}
                        </p>
                      )}
                      <p className="font-body text-xs text-red-500 mt-0.5">
                        {order.payment_method !== 'cod' ? 'Online payment — refund should be processed within 5-7 business days.' : 'COD order — no payment was collected.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Tracking Timeline (inline) */}
                {order.tracking_updates?.length > 0 && (
                  <div className="bg-cream/50 rounded-lg p-3 mb-3">
                    <div className="font-body text-xs font-semibold text-mid-brown mb-2">Tracking:</div>
                    <div className="flex flex-wrap gap-2">
                      {order.tracking_updates.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-white rounded-full px-3 py-1 text-xs font-body text-mid-brown border border-border-custom">
                          <span className={`w-1.5 h-1.5 rounded-full ${i === order.tracking_updates.length - 1 ? 'bg-rust' : 'bg-light-brown'}`}></span>
                          {t.status}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openInvoice(order)} className="font-body text-xs rounded-full" data-testid={`invoice-pdf-${order.id}`}><Download className="w-3 h-3 mr-1" /> View Admin Invoice</Button>

                  {/* Refund button — show for online paid/cancelled/processing orders that haven't been refunded yet */}
                  {order.payment_method !== 'cod' && order.payment_id && !order.refund_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRefundModal(order)}
                      className="font-body text-xs rounded-full border-violet-300 text-violet-700 hover:bg-violet-50 hover:border-violet-400"
                      data-testid={`refund-btn-${order.id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" /> Issue Refund
                    </Button>
                  )}

                  {/* Already refunded badge */}
                  {order.refund_id && (
                    <div className="flex items-center gap-1.5 bg-violet-50 border border-violet-200 rounded-full px-3 py-1">
                      <RotateCcw className="w-3 h-3 text-violet-600" />
                      <span className="font-body text-xs text-violet-700 font-medium">
                        Refunded ₹{order.refund_amount?.toLocaleString('en-IN')} · {order.refund_id}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={!!trackingOpen} onOpenChange={() => setTrackingOpen(null)}>
          <DialogContent className="max-w-sm bg-white">
            <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">Add Tracking Update</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label className="font-body text-sm">Status</Label><Input value={trackStatus} onChange={e => setTrackStatus(e.target.value)} className="mt-1 bg-cream/50" placeholder="e.g. Shipped, Out for Delivery" data-testid="tracking-status-input" /></div>
              <div><Label className="font-body text-sm">Note (optional)</Label><Input value={trackNote} onChange={e => setTrackNote(e.target.value)} className="mt-1 bg-cream/50" placeholder="e.g. Via BlueDart, AWB #12345" /></div>
              <Button onClick={addTracking} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full" data-testid="submit-tracking-btn">Add Update</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Refund Modal */}
        {refundModal.open && refundModal.order && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(28,18,9,0.6)' }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                  <RotateCcw className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-dark-brown">Issue Refund</h2>
                  <p className="font-body text-xs text-light-brown">{refundModal.order.invoice_number} &middot; {refundModal.order.user_email}</p>
                </div>
              </div>

              <div className="bg-cream rounded-xl p-3 mb-4 flex items-center justify-between">
                <span className="font-body text-sm text-mid-brown">Order Total</span>
                <span className="font-body text-sm font-bold text-dark-brown">&#8377;{refundModal.order.total?.toLocaleString('en-IN')}</span>
              </div>

              <div className="mb-4">
                <Label className="font-body text-sm text-dark-brown mb-2 block">Refund Type</Label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${refundType === 'full' ? 'border-violet-400 bg-violet-50' : 'border-border-custom hover:border-violet-200'}`}>
                    <input type="radio" name="refund_type" value="full" checked={refundType === 'full'} onChange={() => { setRefundType('full'); setRefundAmount(''); }} className="accent-violet-600" />
                    <div>
                      <div className="font-body text-sm font-medium text-dark-brown">Full Refund</div>
                      <div className="font-body text-xs text-light-brown">&#8377;{refundModal.order.total?.toLocaleString('en-IN')}</div>
                    </div>
                  </label>
                  <label className={`flex-1 flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${refundType === 'partial' ? 'border-violet-400 bg-violet-50' : 'border-border-custom hover:border-violet-200'}`}>
                    <input type="radio" name="refund_type" value="partial" checked={refundType === 'partial'} onChange={() => setRefundType('partial')} className="accent-violet-600" />
                    <div>
                      <div className="font-body text-sm font-medium text-dark-brown">Partial Refund</div>
                      <div className="font-body text-xs text-light-brown">Custom amount</div>
                    </div>
                  </label>
                </div>
              </div>

              {refundType === 'partial' && (
                <div className="mb-4">
                  <Label className="font-body text-sm text-dark-brown mb-1 block">Refund Amount (&#8377;)</Label>
                  <Input
                    type="number"
                    value={refundAmount}
                    onChange={e => setRefundAmount(e.target.value)}
                    placeholder={`Max: ${refundModal.order.total}`}
                    min="1"
                    max={refundModal.order.total}
                    className="bg-cream/50 font-body"
                  />
                </div>
              )}

              <div className="mb-4">
                <Label className="font-body text-sm text-dark-brown mb-1 block">Reason</Label>
                <Input
                  value={refundReason}
                  onChange={e => setRefundReason(e.target.value)}
                  placeholder="e.g. Customer cancelled, damaged goods..."
                  className="bg-cream/50 font-body"
                />
              </div>

              <div className="bg-violet-50 border border-violet-100 rounded-lg px-3 py-2 mb-5">
                <p className="font-body text-xs text-violet-700">Payment ID: <span className="font-mono font-semibold">{refundModal.order.payment_id}</span></p>
                <p className="font-body text-xs text-violet-500 mt-0.5">Refund will reach customer in 5-7 business days.</p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 font-body text-sm rounded-full border-border-custom" onClick={() => setRefundModal({ open: false, order: null })} disabled={refunding}>
                  Cancel
                </Button>
                <Button
                  className="flex-1 font-body text-sm rounded-full bg-violet-600 hover:bg-violet-700 text-white border-0 disabled:opacity-50"
                  onClick={submitRefund}
                  disabled={refunding}
                >
                  {refunding ? 'Processing...' : `Refund \u20B9${refundType === 'full' ? (refundModal.order.total?.toLocaleString('en-IN')) : (refundAmount || '...')}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
